// Ключи для хранения в localStorage
const STORAGE_KEY = 'vless_recovery_history';
const FAVORITES_KEY = 'vless_favorites';
const NOTES_KEY = 'vless_order_notes';
const STATS_KEY = 'vless_user_stats';
const MAX_HISTORY_ITEMS = 10;

// Получить историю восстановлений
export const getRecoveryHistory = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const history = JSON.parse(stored);
    return Array.isArray(history) ? history : [];
  } catch (error) {
    console.error('Error reading recovery history:', error);
    return [];
  }
};

// Добавить запись в историю
export const addToRecoveryHistory = (orderData) => {
  try {
    const history = getRecoveryHistory();
    
    // Проверяем, есть ли уже такой заказ в истории
    const existingIndex = history.findIndex(
      item => item.orderNumber === orderData.orderNumber
    );
    
    // Создаем новую запись с timestamp
    const newEntry = {
      id: Date.now().toString(),
      ...orderData,
      timestamp: new Date().toISOString(),
      favorite: existingIndex !== -1 ? history[existingIndex].favorite : false,
      note: existingIndex !== -1 ? history[existingIndex].note : '',
      tags: existingIndex !== -1 ? history[existingIndex].tags : []
    };
    
    // Если заказ уже есть, обновляем его
    if (existingIndex !== -1) {
      history[existingIndex] = newEntry;
    } else {
      // Добавляем в начало массива
      history.unshift(newEntry);
      
      // Обновляем статистику
      updateUserStats();
    }
    
    // Ограничиваем размер истории
    const limitedHistory = history.slice(0, MAX_HISTORY_ITEMS);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(limitedHistory));
    return limitedHistory;
  } catch (error) {
    console.error('Error saving recovery history:', error);
    return getRecoveryHistory();
  }
};

// Очистить историю
export const clearRecoveryHistory = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing recovery history:', error);
    return false;
  }
};

// Получить конкретную запись по номеру заказа
export const getRecoveryByOrderNumber = (orderNumber) => {
  try {
    const history = getRecoveryHistory();
    return history.find(item => item.orderNumber === orderNumber);
  } catch (error) {
    console.error('Error getting recovery by order number:', error);
    return null;
  }
};

// Удалить конкретную запись
export const removeFromRecoveryHistory = (orderNumber) => {
  try {
    const history = getRecoveryHistory();
    const filtered = history.filter(item => item.orderNumber !== orderNumber);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    return filtered;
  } catch (error) {
    console.error('Error removing from recovery history:', error);
    return getRecoveryHistory();
  }
};

// ============ ИЗБРАННОЕ ============

// Переключить статус избранного
export const toggleFavorite = (orderNumber) => {
  try {
    const history = getRecoveryHistory();
    const index = history.findIndex(item => item.orderNumber === orderNumber);
    
    if (index !== -1) {
      history[index].favorite = !history[index].favorite;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    }
    
    return history;
  } catch (error) {
    console.error('Error toggling favorite:', error);
    return getRecoveryHistory();
  }
};

// Получить избранные заказы
export const getFavorites = () => {
  try {
    const history = getRecoveryHistory();
    return history.filter(item => item.favorite);
  } catch (error) {
    console.error('Error getting favorites:', error);
    return [];
  }
};

// ============ ЗАМЕТКИ ============

// Добавить или обновить заметку к заказу
export const updateOrderNote = (orderNumber, note) => {
  try {
    const history = getRecoveryHistory();
    const index = history.findIndex(item => item.orderNumber === orderNumber);
    
    if (index !== -1) {
      history[index].note = note;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    }
    
    return history;
  } catch (error) {
    console.error('Error updating note:', error);
    return getRecoveryHistory();
  }
};

// Получить заметку к заказу
export const getOrderNote = (orderNumber) => {
  try {
    const history = getRecoveryHistory();
    const order = history.find(item => item.orderNumber === orderNumber);
    return order ? order.note || '' : '';
  } catch (error) {
    console.error('Error getting note:', error);
    return '';
  }
};

// ============ ЭКСПОРТ/ИМПОРТ ============

// Экспортировать историю в JSON
export const exportHistory = () => {
  try {
    const history = getRecoveryHistory();
    const stats = getUserStats();
    
    const exportData = {
      version: '1.0',
      exportDate: new Date().toISOString(),
      history: history,
      stats: stats
    };
    
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `vless-history-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error exporting history:', error);
    return false;
  }
};

// Импортировать историю из JSON
export const importHistory = (file) => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const importData = JSON.parse(e.target.result);
          
          // Проверяем формат данных
          if (!importData.history || !Array.isArray(importData.history)) {
            reject(new Error('Неверный формат файла'));
            return;
          }
          
          // Объединяем с текущей историей (избегаем дубликатов)
          const currentHistory = getRecoveryHistory();
          const mergedHistory = [...importData.history];
          
          currentHistory.forEach(item => {
            const exists = mergedHistory.find(h => h.orderNumber === item.orderNumber);
            if (!exists) {
              mergedHistory.push(item);
            }
          });
          
          // Сортируем по дате (новые сверху) и ограничиваем размер
          mergedHistory.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          const limitedHistory = mergedHistory.slice(0, MAX_HISTORY_ITEMS);
          
          localStorage.setItem(STORAGE_KEY, JSON.stringify(limitedHistory));
          
          resolve(limitedHistory);
        } catch (error) {
          reject(new Error('Ошибка при чтении файла: ' + error.message));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Ошибка при чтении файла'));
      };
      
      reader.readAsText(file);
    } catch (error) {
      reject(new Error('Ошибка при импорте: ' + error.message));
    }
  });
};

// ============ СТАТИСТИКА ПОЛЬЗОВАТЕЛЯ ============

// Обновить статистику пользователя
const updateUserStats = () => {
  try {
    const stats = getUserStats();
    const today = new Date().toISOString().split('T')[0];
    
    stats.totalRestored = (stats.totalRestored || 0) + 1;
    stats.lastRestoredDate = today;
    
    // Обновляем статистику по дням
    if (!stats.byDate) stats.byDate = {};
    stats.byDate[today] = (stats.byDate[today] || 0) + 1;
    
    // Обновляем серию дней подряд
    if (stats.lastRestoredDate === today) {
      // Уже было восстановление сегодня
      stats.currentStreak = stats.currentStreak || 1;
    } else {
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      if (stats.lastRestoredDate === yesterday) {
        stats.currentStreak = (stats.currentStreak || 1) + 1;
      } else {
        stats.currentStreak = 1;
      }
    }
    
    stats.maxStreak = Math.max(stats.maxStreak || 0, stats.currentStreak || 0);
    
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
    return stats;
  } catch (error) {
    console.error('Error updating user stats:', error);
    return getUserStats();
  }
};

// Получить статистику пользователя
export const getUserStats = () => {
  try {
    const stored = localStorage.getItem(STATS_KEY);
    if (!stored) {
      return {
        totalRestored: 0,
        lastRestoredDate: null,
        currentStreak: 0,
        maxStreak: 0,
        byDate: {}
      };
    }
    
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error getting user stats:', error);
    return {
      totalRestored: 0,
      lastRestoredDate: null,
      currentStreak: 0,
      maxStreak: 0,
      byDate: {}
    };
  }
};

// Очистить статистику
export const clearUserStats = () => {
  try {
    localStorage.removeItem(STATS_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing stats:', error);
    return false;
  }
};

// ============ ПОИСК ============

// Поиск по истории
export const searchHistory = (query) => {
  try {
    if (!query || query.trim() === '') {
      return getRecoveryHistory();
    }
    
    const history = getRecoveryHistory();
    const lowerQuery = query.toLowerCase();
    
    return history.filter(item => {
      const orderMatch = item.orderNumber?.toLowerCase().includes(lowerQuery);
      const noteMatch = item.note?.toLowerCase().includes(lowerQuery);
      const subscriptionMatch = item.subscription?.toLowerCase().includes(lowerQuery);
      const tagsMatch = item.tags?.some(tag => tag.toLowerCase().includes(lowerQuery));
      
      return orderMatch || noteMatch || subscriptionMatch || tagsMatch;
    });
  } catch (error) {
    console.error('Error searching history:', error);
    return [];
  }
};

// ============ ТЕГИ ============

// Обновить теги заказа
export const updateOrderTags = (orderNumber, tags) => {
  try {
    const history = getRecoveryHistory();
    const index = history.findIndex(item => item.orderNumber === orderNumber);
    
    if (index !== -1) {
      history[index].tags = tags;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    }
    
    return history;
  } catch (error) {
    console.error('Error updating tags:', error);
    return getRecoveryHistory();
  }
};

// Получить теги заказа
export const getOrderTags = (orderNumber) => {
  try {
    const history = getRecoveryHistory();
    const order = history.find(item => item.orderNumber === orderNumber);
    return order ? order.tags || [] : [];
  } catch (error) {
    console.error('Error getting tags:', error);
    return [];
  }
};

// Фильтровать по тегам
export const filterByTags = (tags) => {
  try {
    if (!tags || tags.length === 0) {
      return getRecoveryHistory();
    }
    
    const history = getRecoveryHistory();
    return history.filter(item => {
      if (!item.tags || item.tags.length === 0) return false;
      return tags.some(tag => item.tags.includes(tag));
    });
  } catch (error) {
    console.error('Error filtering by tags:', error);
    return [];
  }
};

// Получить все уникальные теги
export const getAllTags = () => {
  try {
    const history = getRecoveryHistory();
    const allTags = new Set();
    
    history.forEach(item => {
      if (item.tags && Array.isArray(item.tags)) {
        item.tags.forEach(tag => allTags.add(tag));
      }
    });
    
    return Array.from(allTags);
  } catch (error) {
    console.error('Error getting all tags:', error);
    return [];
  }
};

